import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'react-feather';
import { toast } from 'react-toastify';
import { telephonyApi } from '../../../Telephony/telephonyApi';
import { isTelephonyManager } from '../../../Telephony/roles';
import { useEmailAccessConfig } from '../../../utils/emailAccessConfig';
import { Page, PageHeader, Card, Button, Badge, Field, TextInput, Table, Loader, EmptyState } from '../../../ui';
import TelephonyAdminOnly from './AdminOnly';
import './telephony.scss';

interface Team {
  id: string;
  key: string;
  name: string;
  timezone: string;
  isActive: boolean;
}

/**
 * Telephony → Teams: create teams and retire them. "Remove" deactivates —
 * calls and agents keep their history; the team just leaves dropdowns and
 * routing. Restricted to the telephony-manager allowlist like Agents/Numbers.
 */
const TelephonyTeamsPage: React.FC = () => {
  useEmailAccessConfig();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ key: '', name: '' });
  const [errors, setErrors] = useState<{ key?: boolean; name?: boolean }>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    telephonyApi<Team[]>('/v1/teams')
      .then(setTeams)
      .catch((e) => toast.error(`Teams: ${e.message}`))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const createTeam = async () => {
    const nextErrors = {
      key: !/^[a-z][a-z0-9-]{1,30}$/.test(form.key.trim()),
      name: form.name.trim().length < 2,
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setSaving(true);
    try {
      await telephonyApi('/v1/teams', {
        method: 'POST',
        body: JSON.stringify({ key: form.key.trim(), name: form.name.trim() }),
      });
      toast.success(`Team ${form.name} created`);
      setForm({ key: '', name: '' });
      setErrors({});
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (team: Team) => {
    try {
      await telephonyApi(`/v1/teams/${encodeURIComponent(team.key)}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !team.isActive }),
      });
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  if (!isTelephonyManager()) {
    return <TelephonyAdminOnly title="Teams" />;
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Telephony"
        title="Teams"
        subtitle={loading ? 'Loading teams…' : `${teams.length} teams · ${teams.filter((t) => t.isActive).length} active`}
        actions={
          <Button variant="secondary" icon={<RotateCcw size={16} aria-hidden />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />

      <Card title="Create team">
        <div className="tel-form-row">
          <Field
            label="Key"
            required
            hint="Lowercase slug used everywhere as teamKey — cannot be changed later"
            error={errors.key ? 'Lowercase slug, e.g. field-sales' : undefined}
          >
            {(ctl) => (
              <TextInput
                {...ctl}
                value={form.key}
                placeholder="e.g. offline"
                onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))}
              />
            )}
          </Field>
          <Field label="Display name" required error={errors.name ? 'Required' : undefined}>
            {(ctl) => (
              <TextInput
                {...ctl}
                value={form.name}
                placeholder="e.g. Offline Centers"
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            )}
          </Field>
          <div className="tel-form-row__submit">
            <Button variant="primary" loading={saving} onClick={createTeam}>
              {saving ? 'Saving…' : 'Create'}
            </Button>
          </div>
        </div>
      </Card>

      <Card flush title="Teams">
        {loading ? (
          <Loader label="Loading teams…" />
        ) : teams.length === 0 ? (
          <div className="tel-flush-pad">
            <EmptyState title="No teams yet" description="Create the first team above." />
          </div>
        ) : (
          <Table caption="Telephony teams">
            <thead>
              <tr>
                <th>Name</th><th>Key</th><th>Timezone</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => (
                <tr key={t.id} className={t.isActive ? undefined : 'tel-inactive'}>
                  <td>{t.name}</td>
                  <td><code>{t.key}</code></td>
                  <td>{t.timezone}</td>
                  <td>
                    <Badge tone={t.isActive ? 'success' : 'danger'}>
                      {t.isActive ? 'active' : 'inactive'}
                    </Badge>
                  </td>
                  <td className="text-nowrap">
                    <Button size="sm" variant="secondary" onClick={() => toggleActive(t)}>
                      {t.isActive ? 'Remove' : 'Restore'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <div className="tel-flush-pad">
          <p className="tel-note">
            Removing a team deactivates it — existing calls and agents keep
            their history; the team leaves dropdowns and routing.
          </p>
        </div>
      </Card>
    </Page>
  );
};

export default TelephonyTeamsPage;
